// src/pages/Admin/DossierInscription/CreateDossierInscriptionModal.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardCheck,
  FaCloudUploadAlt,
  FaExternalLinkAlt,
  FaFileMedical,
  FaFileSignature,
  FaFolderOpen,
  FaIdCard,
  FaImage,
  FaInfoCircle,
  FaLink,
  FaSave,
  FaSearch,
  FaShieldAlt,
  FaSpinner,
  FaTimes,
  FaUser,
  FaUserCheck,
} from "react-icons/fa";

import { createDossierInscription } from "./../../../services/dossierServices";
import { getAllAdherentsList } from "./../../../services/adherentServices";

const todayStr = () => new Date().toISOString().slice(0, 10);

const initialFormData = {
  inscription_adherent_id: "",
  cm_url: "",
  date_cm: "",
  validate_cm: false,
  formulaire_licence_a_signer: "",
  formulaire_licence_signe: "",
  licence_url: "",
  validate_licence: false,
  validate_reglement: false,
  lien_drive: "",
};

const inputClass =
  "min-h-[48px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-violet-400 dark:focus:ring-violet-950/60";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

function isValidURL(value) {
  const clean = String(value || "").trim();

  if (!clean) return true;

  if (
    clean.startsWith("/uploads") ||
    clean.startsWith("data:") ||
    clean.startsWith("blob:")
  ) {
    return true;
  }

  try {
    const url = new URL(clean);
    return !!url.protocol && !!url.hostname;
  } catch {
    return false;
  }
}

const normalize = (value = "") =>
  value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const orNull = (value) => {
  const clean = String(value ?? "").trim();
  return clean === "" ? null : clean;
};

const toAbsolute = (url = "") => {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  if (!cleanUrl.startsWith("/uploads")) return cleanUrl;

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${cleanUrl}`;
};

const getDefaultPhoto = (genre) =>
  genre === "Masculin" ? "/img/judoka_avatar.png" : "/img/judokate_avatar.png";

const getAdherentPhoto = (adherent) => {
  if (!adherent) return "/img/user_avatar.jpg";

  if (adherent?.autorisation_photo === false) {
    return "/img/interdiction.jpg";
  }

  return (
    toAbsolute(
      adherent?.photo_url ||
        adherent?.photoURL ||
        adherent?.photo ||
        adherent?.avatar
    ) || getDefaultPhoto(adherent?.genre)
  );
};

const getAdherentName = (adherent) => {
  if (!adherent) return "Aucun adhérent sélectionné";

  const nom = String(adherent?.nom || "").trim().toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
};

function SectionCard({ icon: Icon, title, description, children, className = "" }) {
  return (
    <section
      className={`relative rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/80 ${className}`}
    >
      <div className="rounded-t-2xl border-b border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm">
            <Icon className="text-sm" />
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">
              {title}
            </h4>

            {description && (
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">{children}</div>
    </section>
  );
}

function PreviewLine({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-1 line-clamp-2 text-sm font-bold text-slate-800 dark:text-white">
        {value || "—"}
      </p>
    </div>
  );
}

function UrlField({
  label,
  name,
  value,
  onChange,
  placeholder = "https://…",
  disabled,
  icon: Icon = FaLink,
}) {
  const invalid = value && !isValidURL(value);

  return (
    <div>
      <label className={labelClass}>{label}</label>

      <div className="relative">
        <Icon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

        <input
          type="text"
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={`${inputClass} pl-9 ${
            invalid
              ? "border-rose-300 focus:border-rose-500 focus:ring-rose-100 dark:border-rose-800 dark:focus:ring-rose-950/60"
              : ""
          }`}
        />
      </div>

      {invalid && (
        <p className="mt-1 text-xs font-semibold text-rose-600 dark:text-rose-300">
          URL invalide. Utilisez un lien complet ou laissez vide.
        </p>
      )}
    </div>
  );
}

function ValidationSwitch({ checked, onChange, label, description, icon: Icon, disabled }) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      className={`flex min-h-[86px] items-center gap-3 rounded-2xl p-4 text-left ring-1 transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 ${
        checked
          ? "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
          : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-800 dark:hover:bg-slate-900"
      }`}
    >
      <span
        className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${
          checked
            ? "bg-emerald-600 text-white"
            : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
        }`}
      >
        {checked ? <FaCheckCircle /> : <Icon />}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold">{label}</span>
        <span className="mt-1 block text-xs leading-relaxed opacity-75">
          {description}
        </span>
      </span>

      <span
        className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition ${
          checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
        }`}
      >
        <span
          className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
            checked ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </span>
    </button>
  );
}

function AdherentComboBox({ list, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focusIndex, setFocusIndex] = useState(0);

  const inputRef = useRef(null);
  const listRef = useRef(null);

  const indexed = useMemo(() => {
    return (Array.isArray(list) ? list : [])
      .map((adherent) => {
        const fullName = getAdherentName(adherent);
        return {
          id: adherent.id,
          adherent,
          fullName,
          saison: adherent?.saison_active || "",
          email: adherent?.email || "",
          _key: normalize(
            `${fullName} ${adherent?.id ?? ""} ${adherent?.email || ""}`
          ),
        };
      })
      .sort((a, b) =>
        a.fullName.localeCompare(b.fullName, "fr", { sensitivity: "base" })
      );
  }, [list]);

  const selected = useMemo(
    () => indexed.find((row) => String(row.id) === String(value)) || null,
    [indexed, value]
  );

  useEffect(() => {
    if (selected && !open) {
      setQuery(selected.fullName);
    } else if (!value && !open) {
      setQuery("");
    }
  }, [value, selected, open]);

  const results = useMemo(() => {
    const cleanQuery = normalize(query);

    if (!cleanQuery) return indexed.slice(0, 50);

    const tokens = cleanQuery.split(/\s+/).filter(Boolean);

    return indexed
      .filter((row) => tokens.every((token) => row._key.includes(token)))
      .slice(0, 80);
  }, [query, indexed]);

  useEffect(() => {
    setFocusIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;

    const active = listRef.current?.querySelector('[data-active="true"]');

    active?.scrollIntoView({ block: "nearest" });
  }, [focusIndex, open]);

  const selectIndex = (index) => {
    const row = results[index];

    if (!row) return;

    onChange?.(row.id);
    setQuery(row.fullName);
    setOpen(false);
  };

  const clearSelection = () => {
    onChange?.("");
    setQuery("");
    setOpen(true);
    inputRef.current?.focus();
  };

  const onKeyDown = (event) => {
    if (!open && (event.key === "ArrowDown" || event.key === "Enter")) {
      setOpen(true);
      event.preventDefault();
      return;
    }

    if (!open) return;

    if (event.key === "ArrowDown") {
      setFocusIndex((index) => Math.min(index + 1, results.length - 1));
      event.preventDefault();
    }

    if (event.key === "ArrowUp") {
      setFocusIndex((index) => Math.max(index - 1, 0));
      event.preventDefault();
    }

    if (event.key === "Enter") {
      selectIndex(focusIndex);
      event.preventDefault();
    }

    if (event.key === "Escape") {
      setOpen(false);
      event.preventDefault();
    }
  };

  return (
    <div className="relative">
      {selected && (
        <div className="mb-3 flex items-center gap-3 rounded-2xl bg-violet-50 p-3 ring-1 ring-violet-200 dark:bg-violet-900/20 dark:ring-violet-800/60">
          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-2xl bg-white ring-1 ring-violet-100 dark:bg-slate-950 dark:ring-violet-800">
            <img
              src={getAdherentPhoto(selected.adherent)}
              alt={selected.fullName}
              className="h-full w-full object-cover"
              onError={(event) => {
                event.currentTarget.src = "/img/user_avatar.jpg";
              }}
            />
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-violet-900 dark:text-violet-100">
              {selected.fullName}
            </p>
            <p className="truncate text-xs text-violet-700/75 dark:text-violet-200/75">
              #{selected.id}
              {selected.saison ? ` · Saison ${selected.saison}` : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={clearSelection}
            disabled={disabled}
            className="rounded-xl bg-white px-3 py-2 text-xs font-bold text-violet-700 ring-1 ring-violet-200 transition hover:bg-violet-100 disabled:opacity-60 dark:bg-slate-950 dark:text-violet-200 dark:ring-violet-800 dark:hover:bg-slate-900"
          >
            Changer
          </button>
        </div>
      )}

      <div className="relative">
        <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

        <input
          ref={inputRef}
          type="text"
          placeholder="Rechercher un adhérent nom, prénom, #id…"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          className={`${inputClass} pl-9`}
        />
      </div>

      {open && !disabled && (
        <div
          ref={listRef}
          role="listbox"
          aria-label="Choisir un adhérent"
          onMouseDown={(event) => event.preventDefault()}
          className="mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
        >
          {results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">
              Aucun résultat.
            </div>
          ) : (
            results.map((row, index) => {
              const active = index === focusIndex;

              return (
                <button
                  key={row.id}
                  type="button"
                  data-active={active ? "true" : undefined}
                  role="option"
                  aria-selected={String(String(row.id) === String(value))}
                  onMouseEnter={() => setFocusIndex(index)}
                  onClick={() => selectIndex(index)}
                  className={`flex w-full items-center gap-3 px-3 py-3 text-left text-sm transition ${
                    active
                      ? "bg-violet-50 dark:bg-violet-900/20"
                      : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  }`}
                >
                  <div className="h-10 w-10 shrink-0 overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
                    <img
                      src={getAdherentPhoto(row.adherent)}
                      alt={row.fullName}
                      className="h-full w-full object-cover"
                      onError={(event) => {
                        event.currentTarget.src = "/img/user_avatar.jpg";
                      }}
                    />
                  </div>

                  <div className="min-w-0">
                    <div className="truncate font-bold text-slate-900 dark:text-white">
                      {row.fullName}{" "}
                      <span className="text-xs font-semibold text-slate-400">
                        #{row.id}
                      </span>
                    </div>

                    <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {row.saison ? `Saison ${row.saison}` : "Saison non renseignée"}
                      {row.email ? ` · ${row.email}` : ""}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function DossierPreviewCard({ adherent, formData, urlErrorsCount }) {
  const validCount = [
    formData.validate_cm,
    formData.validate_licence,
    formData.validate_reglement,
  ].filter(Boolean).length;

  return (
    <section className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm dark:border-violet-950 dark:bg-slate-900">
      <div className="bg-gradient-to-br from-violet-600 to-blue-600 p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
            <FaFolderOpen className="text-xl" />
          </div>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-white/70">
              Dossier inscription
            </p>
            <p className="truncate text-lg font-bold">Création dossier</p>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="flex flex-col items-center text-center">
          <div className="h-32 w-32 overflow-hidden rounded-[2rem] bg-slate-100 shadow-inner ring-4 ring-white dark:bg-slate-800 dark:ring-slate-900">
            {adherent ? (
              <img
                src={getAdherentPhoto(adherent)}
                alt={getAdherentName(adherent)}
                className="h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.src = "/img/user_avatar.jpg";
                }}
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-slate-400">
                <FaUser className="text-3xl" />
                <span className="px-3 text-xs font-semibold">
                  Sélectionnez un adhérent
                </span>
              </div>
            )}
          </div>

          <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
            {adherent ? getAdherentName(adherent) : "Aucun adhérent"}
          </h3>

          {adherent?.saison_active && (
            <span className="mt-2 inline-flex rounded-full bg-violet-50 px-3 py-1 text-xs font-bold text-violet-700 ring-1 ring-violet-200 dark:bg-violet-900/20 dark:text-violet-200 dark:ring-violet-800/60">
              Saison {adherent.saison_active}
            </span>
          )}
        </div>

        <div className="mt-5 rounded-[1.5rem] bg-slate-950 p-4 text-white shadow-lg dark:bg-slate-900 dark:ring-1 dark:ring-slate-800">
          <p className="text-xs font-bold uppercase tracking-wide text-white/50">
            Validations
          </p>
          <p className="mt-1 text-3xl font-bold">{validCount}/3</p>
          <p className="mt-1 text-xs text-white/60">
            Certificat médical, licence et règlement.
          </p>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2">
          <PreviewLine
            label="Adhérent"
            value={adherent ? getAdherentName(adherent) : "—"}
          />
          <PreviewLine
            label="Certificat médical"
            value={formData.validate_cm ? "Validé" : "Non validé"}
          />
          <PreviewLine
            label="Licence"
            value={formData.validate_licence ? "Validée" : "Non validée"}
          />
          <PreviewLine
            label="Règlement"
            value={formData.validate_reglement ? "Validé" : "Non validé"}
          />
        </div>

        {urlErrorsCount > 0 && (
          <div className="mt-4 rounded-2xl bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 ring-1 ring-rose-100 dark:bg-rose-950/30 dark:text-rose-300 dark:ring-rose-900">
            {urlErrorsCount} URL invalide{urlErrorsCount > 1 ? "s" : ""} à corriger.
          </div>
        )}
      </div>
    </section>
  );
}

export default function CreateDossierInscriptionModal({
  isOpen,
  onClose,
  refreshList,
}) {
  const dialogRef = useRef(null);

  const [formData, setFormData] = useState(initialFormData);
  const [adherents, setAdherents] = useState([]);
  const [loadingAdherents, setLoadingAdherents] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedAdherent = useMemo(() => {
    return (
      adherents.find(
        (adherent) =>
          String(adherent.id) === String(formData.inscription_adherent_id)
      ) || null
    );
  }, [adherents, formData.inscription_adherent_id]);

  const urlErrorsCount = useMemo(() => {
    return [
      formData.cm_url,
      formData.formulaire_licence_a_signer,
      formData.formulaire_licence_signe,
      formData.licence_url,
      formData.lien_drive,
    ].filter((url) => !isValidURL(url)).length;
  }, [formData]);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!formData.inscription_adherent_id) return false;
    if (urlErrorsCount > 0) return false;

    return true;
  }, [formData.inscription_adherent_id, submitting, urlErrorsCount]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  const loadAdherents = useCallback(async () => {
    try {
      setLoadingAdherents(true);

      const list = await getAllAdherentsList();
      const rows = Array.isArray(list) ? list : [];

      rows.sort((a, b) =>
        `${a?.nom || ""} ${a?.prenom || ""}`.localeCompare(
          `${b?.nom || ""} ${b?.prenom || ""}`,
          "fr",
          { sensitivity: "base" }
        )
      );

      setAdherents(rows);
    } catch (error) {
      console.error("Erreur lors du chargement des adhérents:", error);
      toast.error("Erreur lors du chargement des adhérents.");
    } finally {
      setLoadingAdherents(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(initialFormData);
    setSubmitting(false);

    loadAdherents();
  }, [isOpen, loadAdherents]);

  useEffect(() => {
    if (!isOpen) return;

    const root = window.document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    const timer = setTimeout(() => {
      dialogRef.current?.querySelector("input[type='text']")?.focus();
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
      root.style.overflow = previousOverflow;
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const onChangeField = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === "validate_cm") {
      setFormData((previous) => ({
        ...previous,
        validate_cm: checked,
        date_cm: checked && !previous.date_cm ? todayStr() : previous.date_cm,
      }));
      return;
    }

    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const setValidation = (name) => {
    setFormData((previous) => {
      const nextChecked = !previous[name];

      if (name === "validate_cm") {
        return {
          ...previous,
          validate_cm: nextChecked,
          date_cm:
            nextChecked && !previous.date_cm ? todayStr() : previous.date_cm,
        };
      }

      return {
        ...previous,
        [name]: nextChecked,
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.inscription_adherent_id) {
      toast.warn("Sélectionnez un adhérent.");
      return;
    }

    const badField = [
      "cm_url",
      "formulaire_licence_a_signer",
      "formulaire_licence_signe",
      "licence_url",
      "lien_drive",
    ].find((key) => !isValidURL(formData[key]));

    if (badField) {
      toast.error(
        "Veuillez saisir des URLs valides ou laisser les champs vides."
      );
      return;
    }

    try {
      setSubmitting(true);

      const id = Number(formData.inscription_adherent_id);

      const onlyId =
        !formData.cm_url &&
        !formData.date_cm &&
        !formData.formulaire_licence_a_signer &&
        !formData.formulaire_licence_signe &&
        !formData.licence_url &&
        !formData.lien_drive &&
        !formData.validate_cm &&
        !formData.validate_licence &&
        !formData.validate_reglement;

      if (onlyId) {
        await createDossierInscription(id);
      } else {
        const payload = {
          cm_url: orNull(formData.cm_url),
          date_cm: formData.date_cm ? formData.date_cm : null,
          validate_cm: !!formData.validate_cm,
          formulaire_licence_a_signer: orNull(
            formData.formulaire_licence_a_signer
          ),
          formulaire_licence_signe: orNull(formData.formulaire_licence_signe),
          licence_url: orNull(formData.licence_url),
          validate_licence: !!formData.validate_licence,
          validate_reglement: !!formData.validate_reglement,
          lien_drive: orNull(formData.lien_drive),
        };

        if (payload.validate_cm && !payload.date_cm) {
          payload.date_cm = todayStr();
        }

        await createDossierInscription(id, payload);
      }

      toast.success("Dossier d'inscription créé avec succès.");

      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la création du dossier", error);

      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Erreur lors de la création du dossier.";

      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Créer un dossier d’inscription"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="relative flex max-h-[94dvh] w-full max-w-7xl flex-col overflow-hidden rounded-t-[2rem] bg-slate-50 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 sm:max-h-[90dvh] sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-violet-600 to-pink-600 px-4 py-5 text-white sm:px-6">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/75">
                Dossiers inscriptions
              </p>

              <h3 className="mt-1 text-xl font-bold text-white sm:text-2xl">
                Créer un dossier d’inscription
              </h3>

              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/85">
                Sélectionnez l’adhérent, ajoutez les liens utiles puis validez
                les pièces du dossier.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 disabled:opacity-60"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="create-dossier-modal-scroll min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[340px_minmax(0,1fr)]">
              <aside className="space-y-5">
                <DossierPreviewCard
                  adherent={selectedAdherent}
                  formData={formData}
                  urlErrorsCount={urlErrorsCount}
                />

                <SectionCard
                  icon={FaInfoCircle}
                  title="Résumé"
                  description="Contrôle rapide avant création."
                >
                  <div className="space-y-3">
                    <PreviewLine
                      label="Adhérent"
                      value={
                        selectedAdherent ? getAdherentName(selectedAdherent) : "—"
                      }
                    />
                    <PreviewLine
                      label="Certificat"
                      value={formData.validate_cm ? "Validé" : "Non validé"}
                    />
                    <PreviewLine
                      label="Licence"
                      value={
                        formData.validate_licence ? "Validée" : "Non validée"
                      }
                    />
                    <PreviewLine
                      label="Règlement"
                      value={
                        formData.validate_reglement ? "Validé" : "Non validé"
                      }
                    />
                  </div>

                  <div className="mt-4 rounded-2xl bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:ring-blue-900">
                    Si seul l’adhérent est renseigné, le dossier sera créé en
                    mode minimal.
                  </div>
                </SectionCard>
              </aside>

              <main className="space-y-5">
                <SectionCard
                  icon={FaUserCheck}
                  title="Adhérent"
                  description="Recherchez et sélectionnez l’adhérent concerné."
                  className="z-40"
                >
                  <label className={labelClass}>
                    Adhérent <span className="text-pink-600">*</span>
                  </label>

                  <AdherentComboBox
                    list={adherents}
                    value={formData.inscription_adherent_id}
                    disabled={loadingAdherents || submitting}
                    onChange={(id) =>
                      setFormData((previous) => ({
                        ...previous,
                        inscription_adherent_id: id ? Number(id) : "",
                      }))
                    }
                  />

                  {loadingAdherents && (
                    <div className="mt-3 flex items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                      <FaSpinner className="animate-spin" />
                      Chargement des adhérents…
                    </div>
                  )}
                </SectionCard>

                <SectionCard
                  icon={FaFileMedical}
                  title="Certificat médical"
                  description="Ajoutez le lien et la date du certificat si nécessaire."
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <UrlField
                        label="URL du certificat médical"
                        name="cm_url"
                        value={formData.cm_url}
                        onChange={onChangeField}
                        disabled={submitting}
                        icon={FaExternalLinkAlt}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>
                        Date du certificat médical
                      </label>

                      <div className="relative">
                        <FaCalendarAlt className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="date"
                          name="date_cm"
                          value={formData.date_cm}
                          onChange={onChangeField}
                          className={`${inputClass} pl-9`}
                          disabled={submitting}
                        />
                      </div>
                    </div>

                    <div className="flex items-end">
                      <ValidationSwitch
                        checked={!!formData.validate_cm}
                        onChange={() => setValidation("validate_cm")}
                        label="Certificat médical validé"
                        description="Coche aussi la date du jour si aucune date n’est renseignée."
                        icon={FaFileMedical}
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaFileSignature}
                  title="Licence et assurance"
                  description="Liens vers le formulaire à signer, le formulaire signé et la licence."
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <UrlField
                      label="Formulaire à signer"
                      name="formulaire_licence_a_signer"
                      value={formData.formulaire_licence_a_signer}
                      onChange={onChangeField}
                      disabled={submitting}
                      icon={FaExternalLinkAlt}
                    />

                    <UrlField
                      label="Formulaire signé"
                      name="formulaire_licence_signe"
                      value={formData.formulaire_licence_signe}
                      onChange={onChangeField}
                      disabled={submitting}
                      icon={FaExternalLinkAlt}
                    />

                    <div className="md:col-span-2">
                      <UrlField
                        label="Licence"
                        name="licence_url"
                        value={formData.licence_url}
                        onChange={onChangeField}
                        disabled={submitting}
                        icon={FaIdCard}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <ValidationSwitch
                        checked={!!formData.validate_licence}
                        onChange={() => setValidation("validate_licence")}
                        label="Licence validée"
                        description="Indique que la partie licence / assurance est conforme."
                        icon={FaFileSignature}
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaFolderOpen}
                  title="Dossier Drive"
                  description="Lien vers le dossier partagé si vous en utilisez un."
                >
                  <UrlField
                    label="Lien Drive"
                    name="lien_drive"
                    value={formData.lien_drive}
                    onChange={onChangeField}
                    disabled={submitting}
                    icon={FaCloudUploadAlt}
                  />
                </SectionCard>

                <SectionCard
                  icon={FaClipboardCheck}
                  title="Validations du dossier"
                  description="Indiquez rapidement les éléments déjà validés."
                >
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                    <ValidationSwitch
                      checked={!!formData.validate_cm}
                      onChange={() => setValidation("validate_cm")}
                      label="Certificat médical"
                      description="CM vérifié et validé."
                      icon={FaFileMedical}
                      disabled={submitting}
                    />

                    <ValidationSwitch
                      checked={!!formData.validate_licence}
                      onChange={() => setValidation("validate_licence")}
                      label="Licence"
                      description="Licence ou formulaire validé."
                      icon={FaFileSignature}
                      disabled={submitting}
                    />

                    <ValidationSwitch
                      checked={!!formData.validate_reglement}
                      onChange={() => setValidation("validate_reglement")}
                      label="Règlement"
                      description="Paiement ou règlement validé."
                      icon={FaShieldAlt}
                      disabled={submitting}
                    />
                  </div>
                </SectionCard>
              </main>
            </div>
          </div>

          <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <FaSave />
                    Créer le dossier
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .create-dossier-modal-scroll::-webkit-scrollbar {
          width: 10px;
        }

        .create-dossier-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.45);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }

        .create-dossier-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .dark .create-dossier-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.35);
          border: 3px solid transparent;
          background-clip: content-box;
        }
      `}</style>
    </div>,
    window.document.body
  );
}